import { debugManager } from '@tunein/web-utils';
import { v4 as uuidv4 } from 'uuid';
const debug = debugManager.createDebugger('APSIFRAME', '[Web Common] Amazon Publisher Services Bidder (IFRAME)');
const SECURITY_TOKEN = uuidv4();
const getIframeHtml = () => `
<!DOCTYPE html>
<html lang="en">
<head>
  <script src="https://c.amazon-adsystem.com/aax2/apstag.js"></script>
  <script>
    const appToken = '${SECURITY_TOKEN}';

    // Because this is a "Friendly Iframe" (about:blank injected with document.write),
    // it perfectly inherits the parent window's origin (e.g., https://tunein.com).
    // We can securely lock down our postMessage calls to our exact origin instead of using '*'.
    const targetOrigin = window.location.origin;

    window.addEventListener('message', function (event) {
      const { type, payload, requestId, token } = event.data;

      if (token !== appToken) {
        return;
      }

      if (type === 'INIT') {
        apstag.init(payload, function () {
          parent.postMessage({ type: 'INIT_COMPLETE', token }, targetOrigin);
        });
        return;
      }

      if (type === 'FETCH_BIDS') {
        apstag.fetchBids(payload, function (bids) {
          parent.postMessage({ type: 'BIDS', bids, requestId, token }, targetOrigin);
        });
        return;
      }

      if (type === 'DEBUG') {
        apstag.debug(payload);
        return;
      }
    });

    // Signal readiness from inside the iframe rather than relying on the parent's
    // \`iframe.onload\`. This inline script is non-async and comes after the non-async
    // apstag.js <script src>, so by the time it runs apstag has parsed and executed.
    // That removes the INIT/apstag race where onload could fire before apstag was ready.
    parent.postMessage({ type: 'READY', token: appToken }, targetOrigin);
  </script>
</head>
<body></body>
</html>
`;
export class ApsIframe {
    #iframe;
    #onReady;
    #iframeReady;
    #initReady;
    #didFail = false;
    #didInitialize = false;
    #isMessageListenerRegistered = false;
    #bidRequestSequence = 0;
    #pendingBidsRequests = new Map();
    constructor() {
        this.#iframeReady = Promise.withResolvers();
        this.#initReady = Promise.withResolvers();
    }
    #registerMessageListener() {
        if (this.#isMessageListenerRegistered) {
            return;
        }
        window.addEventListener('message', this.#onIframeMessage);
        this.#isMessageListenerRegistered = true;
    }
    #onIframeMessage = (event) => {
        const { type, bids = [], requestId, token } = event.data;
        if (token !== SECURITY_TOKEN) {
            return;
        }
        if (type === 'READY') {
            this.#iframeReady.resolve();
            return;
        }
        if (type === 'INIT_COMPLETE') {
            if (this.#didInitialize) {
                return;
            }
            this.#didInitialize = true;
            this.#initReady.resolve();
            this.#onReady?.();
            debug.verbose('APS iframe initialized');
            window.apstagiframe = {
                debug: (enableOrDisable) => {
                    this.#iframe.contentWindow?.postMessage({
                        type: 'DEBUG',
                        payload: enableOrDisable,
                        token: SECURITY_TOKEN,
                    }, window.location.origin);
                },
            };
            return;
        }
        if (type !== 'BIDS' || !requestId) {
            return;
        }
        const pendingBidsRequest = this.#pendingBidsRequests.get(requestId);
        if (!pendingBidsRequest) {
            debug.verbose('Ignoring APS iframe bids response for inactive request', {
                requestId,
                activeRequestIds: [...this.#pendingBidsRequests.keys()],
            });
            return;
        }
        this.#pendingBidsRequests.delete(requestId);
        debug.verbose('Received APS bids from iframe', bids);
        pendingBidsRequest.resolve(bids);
    };
    async init(options, onReady, useBlobFallback = false) {
        debug.verbose('Initializing APS iframe', { useBlobFallback });
        this.#onReady = onReady;
        this.#registerMessageListener();
        this.#iframe = document.createElement('iframe');
        this.#iframe.id = 'aps-iframe';
        this.#iframe.style.display = 'none';
        let blobUrl;
        if (useBlobFallback) {
            const blob = new Blob([getIframeHtml()], { type: 'text/html' });
            blobUrl = URL.createObjectURL(blob);
            this.#iframe.src = blobUrl;
        }
        else {
            this.#iframe.src = 'about:blank';
        }
        const revokeBlobUrl = () => {
            if (blobUrl) {
                URL.revokeObjectURL(blobUrl);
                blobUrl = undefined;
            }
        };
        this.#iframeReady.promise.then(revokeBlobUrl);
        this.#iframe.onerror = () => {
            debug.verbose('APS iframe onerror fired');
            this.#didFail = true;
            this.#iframeReady.resolve();
            this.#initReady.resolve();
            onReady();
        };
        document.body.appendChild(this.#iframe);
        if (!useBlobFallback) {
            const iframeDoc = this.#iframe.contentWindow?.document;
            if (!iframeDoc) {
                debug.verbose('APS iframe has no contentWindow.document; aborting init');
                this.#didFail = true;
                this.#iframeReady.resolve();
                this.#initReady.resolve();
                onReady();
                return;
            }
            iframeDoc.open();
            iframeDoc.write(getIframeHtml());
            iframeDoc.close();
        }
        await this.#iframeReady.promise;
        if (this.#didFail) {
            return;
        }
        this.#iframe.contentWindow?.postMessage({
            type: 'INIT',
            payload: options,
            token: SECURITY_TOKEN,
        }, window.location.origin);
    }
    async fetchBids(options) {
        if (this.#didFail) {
            return;
        }
        await Promise.all([this.#iframeReady.promise, this.#initReady.promise]);
        if (this.#didFail) {
            return;
        }
        const requestId = `bids-${++this.#bidRequestSequence}`;
        const bidsReady = Promise.withResolvers();
        this.#pendingBidsRequests.set(requestId, bidsReady);
        this.#iframe.contentWindow?.postMessage({
            type: 'FETCH_BIDS',
            payload: options,
            requestId,
            token: SECURITY_TOKEN,
        }, window.location.origin);
        return bidsReady.promise;
    }
}
